import type { UserState, UserCollaborator } from 'src/types/user';
import type { Pagination, DashboardUsage } from 'src/types/common';

import { enqueueSnackbar } from 'notistack';

import { ToastOptions } from 'src/app-configs';
import { userModel } from 'src/store/userModel';
import { generalModel } from 'src/store/generalModel';

import api from './api';
import { LoadingWrapper } from './loading-wrapper';

const apis = {
  profile: async () => {
    try {
      const { data } = await api.get<UserState>('/users/profile');
      if (!data) {
        return;
      }
      userModel.setState(data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  },
  updateProfile: async (data: Record<string, any>) => {
    try {
      await api.patch<UserState>('/users/profile', data);
      enqueueSnackbar('Profile updated', { variant: 'success', ...ToastOptions });
      apis.profile();
      return true;
    } catch (error) {
      console.error('Error updating user profile:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error updating user profile', { variant: 'error', ...ToastOptions });
      return false;
    }
  },
  getUserOrgs: async () => {
    try {
      const { data } = await api.get<string[]>('/users/orgs');
      if (!data) {
        return;
      }
      userModel.setState({
        orgs: data,
      });
    } catch (error) {
      console.error('Error fetching user orgs:', error);
    }
  },
  getUserCollaborators: async () => {
    try {
      const { data } = await api.get<Pagination<UserCollaborator>>('/users/collaborators');
      if (!data) {
        return;
      }
      userModel.setState({
        collaborators: data,
      });
    } catch (error) {
      console.error('Error fetching user collaborators:', error);
    }
  },
  sendVerifyEmail: async (email: string) => {
    try {
      await api.post('/users/send-verify-email', { email });
      enqueueSnackbar('Verify email sent', { variant: 'success', ...ToastOptions });
      return true;
    } catch (error) {
      console.error('Error sending verify email:', error);
      const msg = error?.response?.data?.message;
      enqueueSnackbar(Array.isArray(msg) ? msg?.[0] : msg || 'Error sending verify email', { variant: 'error', ...ToastOptions });
      return false;
    }
  },
  verifyEmail: async (token: string) => {
    try {
      await api.post('/users/verify-email', { token });
      await apis.profile();
      enqueueSnackbar('Email verified', { variant: 'success', ...ToastOptions });
      return true;
    } catch (error) {
      console.error('Error verifying email:', error);
      const msg = error?.response?.data?.message;
      enqueueSnackbar(Array.isArray(msg) ? msg?.[0] : msg || 'Error verifying email', { variant: 'error', ...ToastOptions });
      return false;
    }
  },
  changePassword: async (password: string, newPassword: string, confirmNewPassword: string) => {
    try {
      await api.post('/users/change-password', { oldPassword: password, newPassword, confirmNewPassword });
      enqueueSnackbar('Password changed', { variant: 'success', ...ToastOptions });
      return true;
    } catch (error) {
      console.error('Error changing password:', error);
      const msg = error?.response?.data?.message;
      enqueueSnackbar(Array.isArray(msg) ? msg?.[0] : msg || 'Error changing password', { variant: 'error', ...ToastOptions });
      return false;
    }
  },
  getDashboardUsage: async () => {
    try {
      const { data } = await api.get<DashboardUsage>('/users/dashboard/usage');
      generalModel.setState({ dashboardUsage: data });
      return data;
    } catch (error) {
      console.error('Error fetching dashboard usage:', error);
      return null;
    }
  },
};

export const UserApi = LoadingWrapper(apis, userModel);
